'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebounceValue, useEventCallback } from 'usehooks-ts';
import type { PaletteModeId } from './types';
import { CommandItemRenderer, PaletteGroup, PaletteShortcut, PaletteStatusRow, PaletteTargetChip } from './command-item-renderer';
import { extractTextFromCommandLabel } from './command-label-text';
import { CommandPaletteAiSection } from './command-palette-ai-section';
import { DesktopHotkey } from './desktop-hotkey';
import { PALETTE_MODE_LIST, PALETTE_MODES } from './modes';
import { GlobalCommands } from './modules/global-commands';
import { InboxV2PrSearchCommands } from './modules/inbox-v2-pr-search-commands';
import { PrV2AddReviewerCommands } from './modules/pr-v2-add-reviewer-commands';
import { ASK_AI_COMMAND_ID, buildPaletteSections, getContextGroup, getModeSourceState, getPaletteCandidates, getRankingTerm, getRootSearchBreadcrumb, getVisibleCommands, keepShownOrder, SEARCH_PULL_REQUESTS_COMMAND_ID } from './palette-sections';
import { getPullRequestCommandId, useRecentPullRequests } from './recent-pull-requests';
import { useCommands, usePaletteTarget } from './registry';
import { useClipboardPullRequestCommands } from './use-clipboard-pull-request-commands';
import { useCommandPaletteAi } from './use-command-palette-ai';
import { recordCommandUsage, useFuzzySearch, useRecentCommandIds } from './use-fuzzy-search';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { Badge } from '@/components/ui/badge';
import { Command, CommandDialog, CommandInput, CommandList, CommandLoading } from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { COMPOSER_HOTKEY_OPTIONS } from '@/lib/keyboard-shortcuts/composer-hotkey-options';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';
import { useAuth } from '@/providers/auth-provider';

/**
 * Routes where the command bar should never appear.
 */
const EXCLUDED_ROUTES = ['/onboarding', '/sign-in', '/sign-up', '/terms', '/privacy'];
const PALETTE_DESCRIPTION = 'Type to search commands and pull requests. Arrow keys move, Enter runs the highlighted row and Tab asks AI.';

// cmdk sets --cmdk-list-height from the rows it renders, so the list eases to a new height instead
// of jumping when results arrive.
const LIST_CLASS = 'custom-scrollbar h-(--cmdk-list-height) max-h-[min(420px,60vh)] overscroll-contain transition-[height] duration-120 ease-out motion-reduce:transition-none [scrollbar-gutter:stable]';

// cmdk scores every row as it mounts, even with filtering off. A constant score skips that work.
const acceptEveryRow = () => 1;
type ListState = {
  scope: string;
  // Rows ranked when the scope began; rows that arrive later are listed below them.
  shownIds: readonly string[];
  // The row the user moved to, or null to highlight the first row.
  value: string | null;
};

/**
 * Command menu dialog component
 * This is the main UI component for the command system.
 */
export function CommandMenu() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={null}><CommandMenuContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function CommandMenuContent() {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeModeId, setActiveModeId] = useState<PaletteModeId | null>(null);
  const commandInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [desktopHotkeyPresses, setDesktopHotkeyPresses] = useState(0);
  const clipboardCommands = useClipboardPullRequestCommands(open, desktopHotkeyPresses);
  const {
    isDesktopApp
  } = useToDesktop();
  const commands = useCommands();
  const target = usePaletteTarget();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const routeSearchParams: ReadonlyURLSearchParams | undefined = open ? searchParams : undefined;
  // PR v2 command registrations are expensive and route-specific.
  const isCanonicalPrRoute = isPrV2Route(pathname);
  const activeMode = activeModeId ? PALETTE_MODES[activeModeId] : null;
  const effectiveSearchTerm = activeMode?.prefixRegExp ? searchTerm.replace(activeMode.prefixRegExp, '') : searchTerm;
  const enterMode = useCallback((modeId: PaletteModeId, initialSearch = '') => {
    setActiveModeId(modeId);
    setSearchTerm(initialSearch);
  }, []);

  // Exclude certain routes
  const isExcludedRoute = EXCLUDED_ROUTES.some(route => pathname.startsWith(route));

  // Filter out commands that shouldn't show for the current route.
  const visibleCommands = useMemo(() => open ? [...clipboardCommands, ...getVisibleCommands(commands, pathname, routeSearchParams)] : [], [clipboardCommands, commands, open, pathname, routeSearchParams]);

  // Use fuzzy search to get a sorted list
  const candidates = useMemo(() => getPaletteCandidates(visibleCommands, activeMode), [activeMode, visibleCommands]);
  const rankingTerm = getRankingTerm(activeMode, effectiveSearchTerm);
  const rankedCommands = useFuzzySearch(candidates, rankingTerm, {
    boostGroup: getContextGroup(pathname)
  });
  const entryShortcutId = activeMode ? visibleCommands.find(command => command.enterMode === activeMode.id)?.shortcutId : undefined;
  const scope = open ? `${activeModeId ?? 'root'}:${effectiveSearchTerm}` : 'closed';
  const [listState, setListState] = useState<ListState>({
    scope,
    shownIds: [],
    value: null
  });
  let currentList = listState;
  if (listState.scope !== scope) {
    currentList = {
      scope,
      shownIds: rankedCommands.map(command => command.id),
      value: null
    };
    setListState(currentList);
  }
  const recentCommandIds = useRecentCommandIds();
  const {
    user
  } = useAuth();
  const recentPullRequests = useRecentPullRequests(user?.id, 10);
  const {
    sections,
    ordinals
  } = useMemo(() => buildPaletteSections({
    ranked: rankingTerm ? keepShownOrder(rankedCommands, currentList.shownIds) : rankedCommands,
    query: effectiveSearchTerm,
    mode: activeMode,
    pathname,
    recentCommandIds,
    recentPullRequestIds: recentPullRequests.map(pr => getPullRequestCommandId(pr))
  }), [activeMode, currentList.shownIds, effectiveSearchTerm, pathname, rankedCommands, rankingTerm, recentCommandIds, recentPullRequests]);
  const commandsById = useMemo(() => new Map([...visibleCommands, ...sections.flatMap(section => section.commands)].map(command => [command.id, command])), [sections, visibleCommands]);
  const modeSource = activeMode ? getModeSourceState(visibleCommands, activeMode) : null;

  // Track when command menu is opened/closed
  useEffect(() => {
    if (open) {
      trackClientEvent('command_menu_opened', undefined);
    }
  }, [open]);
  const executeCommand = useEventCallback((commandId: string) => {
    const command = commandsById.get(commandId);
    if (!command) {
      return;
    }

    // Mode items are per-entity rows, so keep local usage data bounded and
    // avoid recency boosts reordering a just-toggled row under the cursor.
    if (!command.tag) {
      recordCommandUsage(commandId, effectiveSearchTerm);
    }

    // Track command execution with PostHog. Tagged mode rows report the tag
    // instead of the per-entity id to keep event cardinality bounded.
    trackClientEvent('command_executed', undefined, {
      command_id: command.tag ? `mode:${command.tag}` : commandId,
      command_label: extractTextFromCommandLabel(command.label),
      command_group: command.group || ''
    });

    // An entry's action runs before its mode opens, as "Go to file..." first shows the Changes tab.
    const commandResult = command.action();
    if (command.enterMode && PALETTE_MODES[command.enterMode]) {
      // The search-all row takes the root query along into PR search.
      const carriedSearch = command.id === SEARCH_PULL_REQUESTS_COMMAND_ID ? effectiveSearchTerm : '';
      enterMode(command.enterMode, carriedSearch);
      return;
    }
    if (command.keepOpen || commandResult === false) {
      return;
    }
    setSearchTerm(''); // Reset search whenever command selection triggers navigation/state changes.
    setActiveModeId(null);
    setOpen(false);
  });
  const {
    alternativeCommands: aiAlternativeCommands,
    ask: handleAskAi,
    canAskAi,
    loadingLabel: askAiLoadingLabel,
    reset: resetAiState,
    state: aiState
  } = useCommandPaletteAi({
    candidateCommands: candidates,
    isPrSearchActive: activeMode !== null,
    onExecuteCommand: executeCommand,
    pathname,
    routeSearchParams,
    searchTerm: effectiveSearchTerm,
    visibleCommands
  });

  // The highlight is the row the user moved to while it is still listed, else the first row.
  const aiRowIds = aiState.status === 'idle' ? canAskAi ? [ASK_AI_COMMAND_ID] : [] : aiAlternativeCommands.map(command => command.id);
  const rowIds = [...sections.flatMap(section => section.commands.map(command => command.id)), ...(activeMode ? [] : aiRowIds)];
  const selectedValue = currentList.value !== null && rowIds.includes(currentList.value) ? currentList.value : rowIds[0] ?? '';
  const handleValueChange = useCallback((value: string) => {
    setListState(previous => previous.scope === scope ? {
      ...previous,
      value
    } : previous);
  }, [scope]);

  // A new query or mode lists its best rows first, so show the top of the list.
  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [scope]);
  const resultCount = sections.reduce((count, section) => count + section.commands.filter(command => command.id !== SEARCH_PULL_REQUESTS_COMMAND_ID).length, 0);
  const [resultAnnouncement] = useDebounceValue(open && effectiveSearchTerm.trim() ? `${resultCount === 0 ? 'No' : resultCount} result${resultCount === 1 ? '' : 's'}` : '', 500);

  // Handle selecting a command from the list
  const handleSelectCommand = useEventCallback((commandId: string) => {
    executeCommand(commandId);
    resetAiState();
  });

  // Treat "/" and "#" as mode switches only, never as part of the actual PR query.
  const handleSearchTermChange = useCallback((value: string) => {
    resetAiState();
    if (!activeModeId) {
      const prefixMode = PALETTE_MODE_LIST.find(mode => mode.prefixRegExp?.test(value));
      if (prefixMode?.prefixRegExp) {
        enterMode(prefixMode.id, value.replace(prefixMode.prefixRegExp, ''));
        return;
      }
    }
    setSearchTerm(value);
  }, [activeModeId, enterMode, resetAiState]);

  // Backspace on empty input exits the active mode.
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && canAskAi && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      void handleAskAi();
      return;
    }
    if (!activeModeId && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const keyMode = PALETTE_MODE_LIST.find(mode => mode.triggerKeys?.includes(e.key));
      if (keyMode) {
        e.preventDefault();
        resetAiState();
        enterMode(keyMode.id);
        return;
      }
    }
    if (e.key === 'Backspace' && activeModeId && effectiveSearchTerm === '') {
      e.preventDefault();
      setActiveModeId(null);
      setSearchTerm('');
    }
  }, [activeModeId, canAskAi, effectiveSearchTerm, enterMode, handleAskAi, resetAiState]);
  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchTerm('');
      resetAiState();
      setActiveModeId(null);
    }
  }, [resetAiState]);

  // Escape inside a mode steps back to the root list, as Backspace on an empty search does; at the
  // root it closes the menu.
  const handleEscapeKeyDown = useCallback((event: KeyboardEvent) => {
    if (activeModeId) {
      event.preventDefault();
      setActiveModeId(null);
      setSearchTerm('');
    }
  }, [activeModeId]);

  // Keyboard shortcut to open the command menu (Cmd+K / Ctrl+K)
  useHotkeys('mod+k', e => {
    e.preventDefault();
    handleOpenChange(!open);
  }, {
    ...COMPOSER_HOTKEY_OPTIONS,
    enabled: !isExcludedRoute,
    preventDefault: true
  });

  // Opens rather than toggles, so a press while the palette is open reads the clipboard again. It
  // also leaves any mode or search, since the clipboard command lists only at the root.
  const openFromDesktopHotkey = useCallback(() => {
    setOpen(true);
    setSearchTerm('');
    resetAiState();
    setActiveModeId(null);
    setDesktopHotkeyPresses(presses => presses + 1);
  }, [resetAiState]);
  useEffect(() => {
    if (!open || !commandInputRef.current) {
      return;
    }

    // Wait one frame so Radix/cmdk finish mounting the dialog content before we focus the input.
    const focusFrame = requestAnimationFrame(() => {
      commandInputRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(focusFrame);
    };
  }, [open]);
  useEffect(() => {
    if (open && activeMode) {
      commandInputRef.current?.focus();
    }
  }, [activeMode, open]);
  useEffect(() => {
    if (activeModeId && PALETTE_MODES[activeModeId].clearOnRouteChange) {
      setActiveModeId(null);
      setSearchTerm('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname is the only trigger
  }, [pathname]);

  // If route is excluded, no menu
  if (isExcludedRoute) {
    return null;
  }
  const hasRows = rowIds.length > 0;
  return <>
      {isDesktopApp ? <DesktopHotkey onPress={openFromDesktopHotkey} /> : null}
      {/* Register any sub-command providers. */}
      <GlobalCommands />
      <InboxV2PrSearchCommands isPaletteOpen={open} isSearchModeActive={open && activeModeId === 'pr-search'} searchTerm={effectiveSearchTerm} />
      {isCanonicalPrRoute ? <PrV2AddReviewerCommands isPaletteOpen={open} isActive={open && activeModeId === 'pr-v2-add-reviewer'} /> : null}

      <CommandDialog open={open} onOpenChange={handleOpenChange} onEscapeKeyDown={handleEscapeKeyDown} aria-label="Command Menu" description={PALETTE_DESCRIPTION}>
        <Command shouldFilter={false} filter={acceptEveryRow} loop value={selectedValue} onValueChange={handleValueChange}>
          {/* PR search looks for other pull requests, so the one the page shows doesn't lead it. */}
          {target && activeMode?.rootSearch !== 'pull-requests' && <PaletteTargetChip target={target} />}
          <CommandInput ref={commandInputRef} placeholder={activeMode?.inputPlaceholder ?? 'Search commands and pull requests...'} value={effectiveSearchTerm} onValueChange={handleSearchTermChange} onKeyDown={handleInputKeyDown} wrapperClassName="px-4" className="h-12 text-base" badge={activeMode ? <Badge variant="secondary" className="mr-1.5 shrink-0 rounded-sm text-xs px-1.5 py-0" aria-label={`${activeMode.badgeLabel} - press Escape, or Backspace on empty search, to go back`}>
                  {activeMode.badgeLabel}
                </Badge> : undefined} trailing={entryShortcutId && <PaletteShortcut shortcutId={entryShortcutId} />} />
          <div aria-live="polite" className="sr-only">
            <span>
              {activeMode ? `${activeMode.badgeLabel} - ${activeMode.inputPlaceholder}` : ''}
            </span>
            <span>{resultAnnouncement}</span>
          </div>

          <CommandList ref={listRef} className={LIST_CLASS}>
            {sections.map(section => <PaletteGroup key={section.id} heading={section.heading}>
                {section.commands.map(cmd => <CommandItemRenderer key={cmd.id} command={cmd} selected={cmd.id === selectedValue} breadcrumb={activeMode ? undefined : getRootSearchBreadcrumb(cmd)} ordinal={ordinals.get(cmd.id)} onSelect={handleSelectCommand} />)}
              </PaletteGroup>)}

            {/* Mode empty/loading states */}
            {modeSource && !hasRows && !modeSource.hasLoaded && <CommandLoading>
                <div className="space-y-1 px-1.5 py-1">
                  {['mode-skeleton-1', 'mode-skeleton-2', 'mode-skeleton-3', 'mode-skeleton-4'].map(key => <Skeleton key={key} className="h-9 w-full rounded-md" />)}
                </div>
              </CommandLoading>}
            {modeSource?.isSearchingRemotely && <PaletteGroup>
                <PaletteStatusRow>Searching GitHub...</PaletteStatusRow>
              </PaletteGroup>}
            {modeSource && !hasRows && modeSource.hasLoaded && !modeSource.isSearchingRemotely && <div className="py-6 text-center text-sm text-muted-foreground">
                {modeSource.emptyMessage}
              </div>}

            {!activeMode && <CommandPaletteAiSection alternativeCommands={aiAlternativeCommands} canAskAi={canAskAi} loadingLabel={askAiLoadingLabel} onAskAi={handleAskAi} onSelectCommand={handleSelectCommand} searchTerm={effectiveSearchTerm} selectedValue={selectedValue} state={aiState} />}
          </CommandList>
        </Command>
      </CommandDialog>
    </>;
}